import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Droplet, Trash2 } from "lucide-react";

const TYPES = [
  { v: "fasting", l: "Fasting" },
  { v: "post_breakfast", l: "Post Breakfast" },
  { v: "post_lunch", l: "Post Lunch" },
  { v: "post_dinner", l: "Post Dinner" },
  { v: "random", l: "Random" },
];

function classify(value, type) {
  if (type === "fasting") {
    if (value < 100) return { l: "Normal", c: "emerald" };
    if (value < 126) return { l: "Caution", c: "amber" };
    return { l: "High", c: "rose" };
  }
  if (value < 140) return { l: "Normal", c: "emerald" };
  if (value < 200) return { l: "Caution", c: "amber" };
  return { l: "High", c: "rose" };
}

export default function Sugar() {
  const [logs, setLogs] = useState([]);
  const [type, setType] = useState("fasting");
  const [value, setValue] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/sugar"); setLogs(data); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!value) return;
    setError(""); setLoading(true);
    try { await api.post("/sugar", { type, value: Number(value), date }); setValue(""); await load(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const del = async (id) => { await api.delete(`/sugar/${id}`); await load(); };

  const chartData = [...logs].reverse().map((l) => ({ t: new Date(l.measured_at || l.created_at).toLocaleDateString(), value: l.value, type: l.type }));
  const values = logs.map((l) => l.value);
  const avg = values.length ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 0;
  const hi = values.length ? Math.max(...values) : 0;
  const lo = values.length ? Math.min(...values) : 0;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="sugar-page">
      <PageHeader icon={Droplet} title="Blood Sugar" subtitle="Track your glucose levels across the day" accent="rose" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 stagger">
        <Stat label="Average" value={avg} unit="mg/dL" />
        <Stat label="Highest" value={hi} unit="mg/dL" accent="rose" />
        <Stat label="Lowest" value={lo} unit="mg/dL" accent="emerald" />
      </div>

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="sugar-form">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="mt-1 rounded-xl bg-slate-50" data-testid="sugar-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</Label>
          <Input type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="sugar-date" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Value (mg/dL)</Label>
          <Input type="number" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1 rounded-xl bg-slate-50" placeholder="e.g. 110" data-testid="sugar-value" />
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="sugar-submit">{loading ? "Saving…" : "Add reading"}</Button>
        </div>
        {error && <div className="sm:col-span-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="sugar-error">{error}</div>}
      </form>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="sugar-chart">
        <h3 className="font-heading font-semibold text-slate-800 mb-3">Trend</h3>
        <div className="h-64">
          {chartData.length === 0 ? (
            <div className="h-full grid place-items-center text-slate-400 text-sm">Add readings to see your trend</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="t" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }} />
                <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <h3 className="font-heading font-semibold text-slate-800 mb-3">Recent readings</h3>
      <div className="space-y-2" data-testid="sugar-list">
        {logs.length === 0 && <div className="text-sm text-slate-400">No readings yet.</div>}
        {logs.map((l) => {
          const cat = classify(l.value, l.type);
          return (
            <div key={l.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900 capitalize">{l.type.replace(/_/g, " ")}</div>
                <div className="text-xs text-slate-500">{new Date(l.measured_at || l.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full bg-${cat.c}-50 text-${cat.c}-700 border border-${cat.c}-200`}>{cat.l}</span>
                <div className="text-lg font-bold text-slate-900">{l.value}<span className="text-slate-400 text-xs font-medium ml-1">mg/dL</span></div>
                <button onClick={() => del(l.id)} data-testid={`sugar-del-${l.id}`} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PageHeader({ icon: Icon, title, subtitle, accent = "blue" }) {
  const bg = { blue: "bg-blue-50 text-blue-600", rose: "bg-rose-50 text-rose-600", emerald: "bg-emerald-50 text-emerald-600", violet: "bg-violet-50 text-violet-600", amber: "bg-amber-50 text-amber-600" }[accent];
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className={`w-12 h-12 rounded-2xl ${bg} grid place-items-center`}><Icon className="w-6 h-6" /></div>
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
    </div>
  );
}

export function Stat({ label, value, unit, accent = "blue" }) {
  const color = { blue: "text-blue-700", rose: "text-rose-700", emerald: "text-emerald-700", amber: "text-amber-700" }[accent];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="text-xs uppercase font-bold tracking-wider text-slate-400">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${color}`}>{value}<span className="text-slate-400 text-sm font-medium ml-1">{unit}</span></div>
    </div>
  );
}
